<template>
  <div class="audio-player">
    <div class="controls-row" ref="row">
      <v-btn
        ref="btn"
        icon
        variant="text"
        :color="btnColor"
        :size="btnSize"
        :ripple="false"
        :disabled="!hasAudio"
        @click="togglePlay"
        density="comfortable"
      >
        <v-icon :icon="isPlaying ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline'"></v-icon>
      </v-btn>
      <canvas ref="canvas" :style="{ height: canvHeight + 'px', width: '100%' }" />
    </div>
    <!-- Note: src will be set to an Object URL backed by MediaSource -->
    <audio ref="player" playsinline :controls="false" class="hidden-audio" crossorigin="anonymous" preload="auto" />
  </div>
</template>

<script lang="ts">
import { ref, type Ref, type ComponentPublicInstance, nextTick } from 'vue'
import { Options, Vue } from 'vue-class-component'
import { useAVLine } from 'vue-audio-visual'

type LineColor = string | string[]

/**
 * StreamingAudioPlayer
 * - Plays near-live audio via MediaSource/SourceBuffer by appending binary chunks
 * - Exposes public methods:
 *    - appendChunk(b64: string): void    // append base64-encoded audio bytes
 *    - endStream(): void                 // signal end of stream (closes MediaSource once buffer drains)
 *
 * Design constraints:
 * - Continues to use vue-audio-visual composable (useAVLine) for visualization, bound to the same <audio> element
 * - Keeps UI/UX aligned with AudioPlayer.vue
 */
@Options({
  components: {},
  props: {
    canvHeight: { type: Number, default: 40 },
    canvWidth: { type: Number, default: 300 },
    lineColor: { type: [String, Array], default: '#64B5F6' },
    btnColor: { type: String, default: 'primary' },
    btnSize: { type: String, default: 'large' },
    fftSize: { type: Number, default: 512 },
    autoPlay: { type: Boolean, default: true },
    /**
     * MIME to initialize MediaSource/SourceBuffer with.
     * Recommended: 'audio/webm; codecs=opus' for streaming
     * (Coordinate server to provide Opus/WebM for best compatibility)
     */
    mimeType: { type: String, default: 'audio/webm; codecs=opus' },
    /**
     * If true, attempt to start playback after the first appended chunk (subject to browser autoplay policies).
     */
    startOnFirstChunk: { type: Boolean, default: true },
    /**
     * If true, detect codec from first chunk (MP3 ID3/frame sync -> 'audio/mpeg') and configure SourceBuffer accordingly.
     */
    autoDetectMime: { type: Boolean, default: true },
    /**
     * If true, when no supported MSE MIME is available, accumulate chunks and play at end via Blob URL (non-live fallback).
     */
    enableBlobFallback: { type: Boolean, default: true },
    /**
     * If true, decode via WebAudio (AudioContext) instead of using the <audio> element.
     * This bypasses the media element and will disable the built-in visualizer which binds to <audio>.
     * Keep false to ensure the visualizer works.
     */
    useWebAudioDecode: { type: Boolean, default: false }
  }
})
export default class StreamingAudioPlayer extends Vue {
  // Props
  canvHeight!: number
  canvWidth!: number
  lineColor!: LineColor
  btnColor!: string
  btnSize!: string
  fftSize!: number
  autoPlay!: boolean
  mimeType!: string
  startOnFirstChunk!: boolean
  autoDetectMime!: boolean
  enableBlobFallback!: boolean
  useWebAudioDecode!: boolean

  // Playback state
  private playing_ = false
  get isPlaying(): boolean {
    return this.playing_
  }

  // Audio availability state
  get hasAudio(): boolean {
    return this.totalBytesReceived > 0
  }

  declare $refs: {
    row: HTMLDivElement
    btn: ComponentPublicInstance | HTMLElement
    player: HTMLAudioElement
    canvas: HTMLCanvasElement
  }

  // Visualizer instance and responsive handling
  private avInstance: unknown | null = null
  private avInitialized = false
  private resizeObserver?: ResizeObserver

  // MediaSource pipeline
  private mediaSource: MediaSource | null = null
  private sourceBuffer: SourceBuffer | null = null
  private objectUrl: string | null = null
  private appendQueue: Uint8Array[] = []
  private chosenMime: string | null = null
  private fallbackMode = false
  private fallbackChunks: Uint8Array[] = []
  private sourceOpen = false
  private endRequested = false
  private firstAppended = false
  private isEnded = false
  private chunkCount = 0
  private mseAppends = 0
  private audioCtx: (AudioContext | null) = null
  private webAudioSource: (AudioBufferSourceNode | null) = null
  private totalBytesReceived = 0
  private fallbackBytesTotal = 0
  private fallbackBuffer: Uint8Array | null = null
  private preferBlob: boolean = true

  // Width management
  private getAvailableCanvasWidth(): number {
    const canvas = this.$refs?.canvas
    if (!canvas) return this.canvWidth
    const w = canvas.getBoundingClientRect().width
    return w > 0 ? w : this.canvWidth
  }

  private setCanvasSize(width: number, height: number): void {
    const canvas = this.$refs?.canvas
    if (!canvas) return
    const w = Math.max(0, Math.floor(width))
    const h = Math.max(0, Math.floor(height))
    canvas.width = w
    canvas.height = h
    canvas.style.width = `${w}px`
    canvas.style.height = `${h}px`
  }

  private initVisualizer(): void {
    const player = this.$refs?.player
    const canvas = this.$refs?.canvas
    if (!player || !canvas) return

    // Bind refs used by useAVLine (local Refs per init)
    const playerRef = ref<HTMLAudioElement | null>(player)
    const canvasRef = ref<HTMLCanvasElement | null>(canvas)

    const width = this.getAvailableCanvasWidth() - 20
    this.setCanvasSize(width, this.canvHeight)

    // Single initialization guard to avoid re-creating MediaElementSourceNode
    if (this.avInstance || this.avInitialized) {
      return
    }
    this.avInitialized = true

    // Initialize visualizer. Note: we do not pass 'src' here; analyser binds to player element.
    this.avInstance = useAVLine(playerRef as unknown as never, canvasRef as unknown as never, {
      canvHeight: this.canvHeight,
      canvWidth: width,
      lineColor: this.lineColor,
      fftSize: this.fftSize,
      audioControls: false
    })
  }

  private initMediaSource(): void {
    const audio = this.$refs?.player
    if (!audio) return

    // Prefer MSE progressive playback; Blob fallback is handled dynamically by auto-detect
    this.preferBlob = false

    // Cleanup previous
    this.teardownMediaSource()

    if (this.preferBlob) {
      // Blob mode: do not initialize MediaSource; accumulate bytes and set Blob at finalize
      try { if (this.autoPlay) audio.muted = true } catch { /* ignore */ }
      // Clear any prior src; we'll set Blob URL at finalize
      try { audio.src = '' } catch { /* ignore */ }
      // eslint-disable-next-line no-console
      console.log('[StreamingAudioPlayer] initMediaSource blob-mode', { mimeType: this.mimeType })
      // Reset state flags relevant to MSE
      this.mediaSource = null
      this.objectUrl = null
      this.sourceOpen = false
      return
    }

    // MSE mode
    // Create MediaSource and object URL
    try { if (this.autoPlay) audio.muted = true } catch { /* ignore */ }
    this.mediaSource = new MediaSource()
    this.objectUrl = URL.createObjectURL(this.mediaSource)
    audio.src = this.objectUrl
    // eslint-disable-next-line no-console
    console.log('[StreamingAudioPlayer] initMediaSource set src', { objectUrl: this.objectUrl })

    // Reset flags
    this.isEnded = false
    this.endRequested = false
    this.sourceOpen = false

    const onSourceEnded = (): void => {
      this.isEnded = true
    }
    const onSourceClose = (): void => {
      this.isEnded = true
    }

    const onSourceOpen = (): void => {
      if (!this.mediaSource) return
      this.sourceOpen = true
      // eslint-disable-next-line no-console
      console.log('[StreamingAudioPlayer] sourceopen', { readyState: this.mediaSource.readyState })
      try {
        // Defer SourceBuffer creation until we see the first bytes (for codec auto-detection)
        this.processQueue()
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed during sourceopen handling', e)
      }
    }

    // Attach event
    this.mediaSource.addEventListener('sourceopen', onSourceOpen, { once: true })
    this.mediaSource.addEventListener('sourceended', onSourceEnded)
    this.mediaSource.addEventListener('sourceclose', onSourceClose)
  }

  private onUpdateEnd = (): void => {
    // eslint-disable-next-line no-console
    console.log('[StreamingAudioPlayer] updateend', { queue: this.appendQueue.length, endRequested: this.endRequested })
    // Try appending next queued chunk
    this.processQueue()
    // End stream if requested and queue drained
    if (
      this.endRequested &&
      this.mediaSource &&
      this.sourceBuffer &&
      !this.sourceBuffer.updating &&
      this.appendQueue.length === 0 &&
      this.mediaSource.readyState === 'open'
    ) {
      try {
        this.mediaSource.endOfStream()
        this.isEnded = true
      } catch {
        /* ignore */
      }
    }
  }

  private processQueue(): void {
    if (this.isEnded) {
      // Drop any stale queued data if already ended
      this.appendQueue = []
      return
    }
    if (!this.mediaSource || this.mediaSource.readyState !== 'open') return

    // Lazily initialize SourceBuffer (enables codec auto-detection)
    if (!this.sourceBuffer && this.appendQueue.length > 0) {
      const first = this.appendQueue[0]
      if (!this.ensureSourceBufferInitialized(first)) return
    }

    if (!this.sourceBuffer || this.sourceBuffer.updating) return

    const next = this.appendQueue.shift()
    if (next) {
      try {
        // Ensure we pass an ArrayBuffer (not possibly SharedArrayBuffer) to satisfy BufferSource
        const copy = new ArrayBuffer(next.byteLength)
        new Uint8Array(copy).set(next)
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] appendBuffer', { bytes: next.byteLength })
        this.mseAppends++
        this.sourceBuffer.appendBuffer(copy)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('appendBuffer failed', e)
      }
    }
  }

  private base64ToUint8Array(b64: string): Uint8Array {
    // Normalize base64:
    // - Remove whitespace/newlines
    // - Convert URL-safe base64 to standard
    // - Pad with '=' to length % 4 === 0
    let s = b64.replace(/\s+/g, '').replace(/-/g, '+').replace(/_/g, '/')
    const pad = s.length % 4
    if (pad === 2) s += '=='
    else if (pad === 3) s += '='
    else if (pad === 1) {
      // eslint-disable-next-line no-console
      console.warn('Invalid base64 length; dropping last character to recover')
      s = s.slice(0, -1)
      const pad2 = s.length % 4
      if (pad2 === 2) s += '=='
      else if (pad2 === 3) s += '='
    }

    let binary = ''
    try {
      binary = atob(s)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('base64 decode failed', e)
      return new Uint8Array()
    }

    const len = binary.length
    const bytes = new Uint8Array(len)
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i)
    return bytes
  }

  /**
   * Detect MIME type from the first few bytes.
   * - ID3 header or MPEG frame sync -> audio/mpeg
   * - Fallback to configured mimeType
   */
  private detectMimeFromBytes(bytes: Uint8Array): string {
    // MP3: 'ID3' tag or MPEG frame sync 0xFFE?
    if (bytes.length >= 3 && bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
      return 'audio/mpeg'
    }
    if (bytes.length >= 2 && bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0) {
      return 'audio/mpeg'
    }
    // Ogg/Opus: 'OggS' header
    if (bytes.length >= 4 && bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
      return 'audio/ogg; codecs=opus'
    }
    // WebM (Matroska): EBML header 0x1A45DFA3
    if (bytes.length >= 4 && bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
      return 'audio/webm; codecs=opus'
    }
    // MP4/ISO BMFF: 'ftyp' at offset 4
    if (bytes.length >= 8 && bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
      return 'audio/mp4; codecs="mp4a.40.2"'
    }
    // WAV: 'RIFF' ... 'WAVE'
    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 &&
      bytes[8] === 0x57 && bytes[9] === 0x41 && bytes[10] === 0x56 && bytes[11] === 0x45
    ) {
      return 'audio/wav'
    }
    // Unknown bytes
    return ''
  }

  /**
   * Ensure SourceBuffer is created with an appropriate MIME (auto-detected if enabled).
   * Returns true if ready, false otherwise.
   */
  private ensureSourceBufferInitialized(firstBytes?: Uint8Array): boolean {
    if (this.sourceBuffer) return true
    if (!this.mediaSource || this.mediaSource.readyState !== 'open') return false
    if (!('MediaSource' in window)) return false

    // Choose MIME
    let chosen = this.chosenMime
    if (!chosen) {
      const detected = this.autoDetectMime && firstBytes ? this.detectMimeFromBytes(firstBytes) : null
      if (firstBytes) {
        const head = Array.from(firstBytes.slice(0, 16)).map((b) => b.toString(16).padStart(2, '0')).join(' ')
        // eslint-disable-next-line no-console
        console.log('Audio first bytes', head)
      }

      // If we positively detected a specific container/codec
      if (detected) {
        // Force fallback for MP3. MSE for 'audio/mpeg' is fragile and often results in silence.
        if (detected === 'audio/mpeg' && this.enableBlobFallback) {
          this.fallbackMode = true
          this.transferQueueToFallback()
          // eslint-disable-next-line no-console
          console.log('Detected MP3; forcing Blob fallback (accumulate, play on end)')
          return false
        }
        // Ogg/Opus is generally unsupported by MSE: fallback to Blob playback
        if (detected.startsWith('audio/ogg')) {
          if (this.enableBlobFallback) {
            this.fallbackMode = true
            this.transferQueueToFallback()
            // eslint-disable-next-line no-console
            console.warn(`Detected Ogg/Opus. Using Blob fallback for playback. detected=${detected}`)
            return false
          }
        }
        // If the detected MIME itself is NOT supported by MSE (e.g., audio/mpeg on most browsers),
        // do NOT remap to a different MIME (mismatch would cause silence). Fallback to Blob playback.
        if (!MediaSource.isTypeSupported(detected)) {
          if (this.enableBlobFallback) {
            this.fallbackMode = true
            this.transferQueueToFallback()
            // eslint-disable-next-line no-console
            console.warn(`Detected ${detected} but MSE does not support it. Falling back to Blob playback.`)
            return false
          }
        }

        // Detected and supported -> use it as chosen
        chosen = detected
      } else {
        // No recognizable container signature. Avoid MSE to prevent mismatched container (silent playback).
        if (this.enableBlobFallback) {
          this.fallbackMode = true
          this.transferQueueToFallback()
          // eslint-disable-next-line no-console
          console.warn('Unknown audio container bytes; falling back to Blob playback.')
        }
        return false
      }

      // eslint-disable-next-line no-console
      console.log('StreamingAudioPlayer MSE init', { detected, chosen, mseSupported: MediaSource.isTypeSupported(chosen) })
      this.chosenMime = chosen
    }

    try {
      this.sourceBuffer = this.mediaSource.addSourceBuffer(chosen)
      this.sourceBuffer.addEventListener('updateend', this.onUpdateEnd)
      return true
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('addSourceBuffer failed', e)
      return false
    }
  }

  /**
   * Public API: append a base64-encoded audio chunk
   */
  public appendChunk(b64: string): void {
    if (!b64) return
    const bytes = this.base64ToUint8Array(b64)
    this.totalBytesReceived += bytes.byteLength
    // Mirror every chunk into fallback buffer up front to prevent zero-byte Blob on finalize
    try { this.appendToFallback(bytes) } catch { /* ignore */ }
    // eslint-disable-next-line no-console
    console.log('[StreamingAudioPlayer] appendChunk', { decodedBytes: bytes.byteLength, totalBytes: this.totalBytesReceived, chunk: this.chunkCount + 1, fallbackMode: this.fallbackMode })
    this.chunkCount++
    this.appendBytes(bytes)
    if (!this.firstAppended) {
      this.firstAppended = true
      // Unmute at first audio once playback has been initiated muted (autoplay policy workaround)
      const audio = this.$refs.player
      if (audio) {
        try { audio.muted = false } catch { /* ignore */ }
      }
      if (this.autoPlay && this.startOnFirstChunk) {
        // Delay playback by 100ms to allow element state to settle
        window.setTimeout(() => {
          const a = this.$refs.player
          // eslint-disable-next-line no-console
          console.log('[StreamingAudioPlayer] starting playback after 100ms', { muted: a?.muted, srcSet: !!a?.src })
          this.playAudio()
        }, 100)

        // Watchdog: if after 250ms we still have no SourceBuffer and not in fallback, force fallback
        window.setTimeout(() => {
          if (!this.sourceBuffer && !this.fallbackMode) {
            // Move any queued bytes into fallback and continue accumulating
            this.fallbackMode = true
            this.transferQueueToFallback()
            console.warn('[StreamingAudioPlayer] Watchdog forced Blob fallback (no MSE buffer initialized)')
          }
        }, 250)
      }
    }
  }

  private appendBytes(bytes: Uint8Array): void {
    // Drop any new data after end-of-stream has been requested or reached
    if (this.endRequested || this.isEnded) return

    // Blob-preferred path: always accumulate; do not use MSE queueing at all
    if (this.preferBlob) {
      this.appendToFallback(bytes)
      return
    }

    // Early container detection on first bytes: force Blob/WebAudio fallback for Ogg Opus or MP3
    // to avoid unsupported/silent MSE behavior. Begin accumulating immediately.
    if (!this.sourceBuffer && !this.chosenMime && this.autoDetectMime && bytes && bytes.byteLength > 0) {
      const detected = this.detectMimeFromBytes(bytes)
      if (detected && (detected.startsWith('audio/ogg') || detected === 'audio/mpeg')) {
        this.fallbackMode = true
        // Move any already queued bytes into fallback and include current bytes
        this.transferQueueToFallback()
        this.appendToFallback(bytes)
        // eslint-disable-next-line no-console
        console.warn('[StreamingAudioPlayer] Forcing Blob/WebAudio path based on detected container', { detected, addedBytes: bytes.byteLength })
        return
      }
    }

    // If in fallback mode, accumulate for end-of-stream Blob playback
    if (this.fallbackMode) {
      // eslint-disable-next-line no-console
      console.log('[StreamingAudioPlayer] fallback accumulate', { bytes: bytes.byteLength })
      this.appendToFallback(bytes)
      return
    }

    // Mirror bytes into fallback buffer so we can always finalize a Blob if MSE never appends
    if (!this.fallbackMode) {
      // Mirror into fallbackBuffer so implicit fallback has data even if MSE never appends
      this.appendToFallback(bytes)
    }

    // If MediaSource not yet open, queue and wait for sourceopen
    if (!this.sourceOpen) {
      this.appendQueue.push(bytes)
      console.log('[StreamingAudioPlayer] queued before sourceopen', { queued: this.appendQueue.length })
      return
    }

    // Queue bytes for MSE
    this.appendQueue.push(bytes)
    // If source is open but buffer not yet created, initialize (may auto-detect codec)
    if (!this.sourceBuffer) {
      this.ensureSourceBufferInitialized(bytes)
    }
    this.processQueue()
  }

  /**
   * Public API: signal end of stream (after last chunk)
   * MediaSource will be closed once buffer drains.
   */
  public endStream(): void {
    this.endRequested = true

    // Fallback: assemble Blob and play once (non-live)
    if (this.fallbackMode) {
      try {
        // Ensure any queued MSE bytes are included in the fallback payload
        this.transferQueueToFallback()
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] fallback finalize (explicit)', { fallbackChunks: this.fallbackChunks.length, appendQueue: this.appendQueue.length, totalBytes: this.totalBytesReceived, fallbackBytesTotal: this.fallbackBytesTotal })
        const bytes = (this.fallbackBuffer && this.fallbackBuffer.byteLength > 0)
          ? this.fallbackBuffer
          : this.concatChunks(this.fallbackChunks)
        const mime = this.detectMimeFromBytes(bytes) || this.mimeType
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] finalize(explicit) buffers', { bufferLen: this.fallbackBuffer?.byteLength || 0, chunkCount: this.fallbackChunks.length, bytesLen: bytes.byteLength })
        // Ensure BlobPart is ArrayBuffer (not a view or ArrayBufferLike)
        const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
        // Try WebAudio decode path first for arbitrary codecs/containers
        try {
          const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
            || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
          if (this.useWebAudioDecode && Ctx) {
            if (!this.audioCtx) this.audioCtx = new Ctx()
            const ctx = this.audioCtx as AudioContext
            // decodeAudioData Promise form
            ctx.decodeAudioData(ab.slice(0)).then((buffer: AudioBuffer) => {
              try {
                if (this.webAudioSource) {
                  try { this.webAudioSource.stop(0) } catch { /* ignore */ }
                }
                const src = ctx.createBufferSource()
                src.buffer = buffer
                src.connect(ctx.destination)
                this.webAudioSource = src
        // eslint-disable-next-line no-console
        console.log('Playing via WebAudio decode (fallback)', { duration: buffer.duration })
        src.addEventListener('ended', this.onWebAudioEnded, { once: true })
        src.start(0)
        this.isEnded = false
        this.playing_ = true
              } catch (e) {
                // eslint-disable-next-line no-console
                console.error('WebAudio start failed', e)
              }
            }).catch((_err) => {
              // Fallback to <audio> Blob if decode fails
              const blob = new Blob([ab], { type: mime })
              // eslint-disable-next-line no-console
              console.log('Finalizing Blob fallback', { mime, bytes: bytes.byteLength })
              const audio = this.$refs.player
              if (audio) {
                try {
                  try { audio.pause() } catch { /* ignore */ }
                  try { audio.currentTime = 0 } catch { /* ignore */ }
                  const url = URL.createObjectURL(blob)
                  if (this.objectUrl) { try { URL.revokeObjectURL(this.objectUrl) } catch { /* ignore */ } }
                  this.objectUrl = url
                  audio.src = url
                  audio.load()
                  try { audio.muted = false } catch { /* ignore */ }
                } catch { /* ignore */ }
                this.playAudio()
              }
              this.isEnded = true
            })
            // Exit; playing will be handled in the promise branch
            return
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('WebAudio init failed', e)
        }
        // If no WebAudio, use <audio> Blob fallback
        const blob = new Blob([ab], { type: mime })
        // eslint-disable-next-line no-console
        console.log('Finalizing Blob fallback', { mime, bytes: bytes.byteLength })
        const audio = this.$refs.player
        if (audio) {
          try {
            try { audio.pause() } catch { /* ignore */ }
            try { audio.currentTime = 0 } catch { /* ignore */ }
            const url = URL.createObjectURL(blob)
            audio.src = url
            audio.load()
            try { audio.muted = false } catch { /* ignore */ }
          } catch { /* ignore */ }
          this.playAudio()
        }
        this.isEnded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to finalize fallback Blob playback', e)
      } finally {
        // clear accumulation
        this.fallbackChunks = []
        this.fallbackBuffer = null
        this.appendQueue = []
      }
      return
    }

    // If MSE path never actually appended anything, finalize via Blob even if fallbackMode was not set
    if (!this.sourceBuffer || this.mseAppends === 0) {
      try {
        // Use mirrored fallback chunks (already accumulated during streaming)
        this.transferQueueToFallback()
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] fallback finalize (implicit)', { fallbackChunks: this.fallbackChunks.length, appendQueue: this.appendQueue.length, totalBytes: this.totalBytesReceived, fallbackBytesTotal: this.fallbackBytesTotal })
        const bytes = (this.fallbackBuffer && this.fallbackBuffer.byteLength > 0)
          ? this.fallbackBuffer
          : this.concatChunks(this.fallbackChunks)
        const mime = this.detectMimeFromBytes(bytes) || this.mimeType
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] finalize(implicit) buffers', { bufferLen: this.fallbackBuffer?.byteLength || 0, chunkCount: this.fallbackChunks.length, bytesLen: bytes.byteLength })
        const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
        // Try WebAudio decode first
        try {
          const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
            || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
          if (this.useWebAudioDecode && Ctx) {
            if (!this.audioCtx) this.audioCtx = new Ctx()
            const ctx = this.audioCtx as AudioContext
            ctx.decodeAudioData(ab.slice(0)).then((buffer: AudioBuffer) => {
              try {
                if (this.webAudioSource) {
                  try { this.webAudioSource.stop(0) } catch { /* ignore */ }
                }
                const src = ctx.createBufferSource()
                src.buffer = buffer
                src.connect(ctx.destination)
                this.webAudioSource = src
                // eslint-disable-next-line no-console
                console.log('Playing via WebAudio decode (implicit fallback)', { duration: buffer.duration })
                src.addEventListener('ended', this.onWebAudioEnded, { once: true })
                src.start(0)
                this.isEnded = false
                this.playing_ = true
              } catch (e) {
                console.error('WebAudio start failed', e)
              }
            }).catch((_err) => {
              // Then try <audio> Blob
              const blob = new Blob([ab], { type: mime })
              // eslint-disable-next-line no-console
              console.log('[StreamingAudioPlayer] Finalizing implicit Blob fallback', { mime, bytes: bytes.byteLength, mseAppends: this.mseAppends })
              const audio = this.$refs.player
              if (audio) {
                try {
                  try { audio.pause() } catch { /* ignore */ }
                  try { audio.currentTime = 0 } catch { /* ignore */ }
                  const url = URL.createObjectURL(blob)
                  if (this.objectUrl) { try { URL.revokeObjectURL(this.objectUrl) } catch { /* ignore */ } }
                  this.objectUrl = url
                  audio.src = url
                  audio.load()
                  try { audio.muted = false } catch { /* ignore */ }
                } catch { /* ignore */ }
                this.playAudio()
              }
              this.isEnded = true
            })
            return
          }
        } catch (e) {
          console.error('WebAudio init failed', e)
        }
        // Fallback to Blob on <audio>
        const blob = new Blob([ab], { type: mime })
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] Finalizing implicit Blob fallback', { mime, bytes: bytes.byteLength, mseAppends: this.mseAppends })
        const audio = this.$refs.player
        if (audio) {
          try {
            try { audio.pause() } catch { /* ignore */ }
            try { audio.currentTime = 0 } catch { /* ignore */ }
            const url = URL.createObjectURL(blob)
                  audio.src = url
                  audio.load()
                  try { audio.muted = false } catch { /* ignore */ }
          } catch { /* ignore */ }
          this.playAudio()
        }
        this.isEnded = true
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to finalize implicit Blob fallback', e)
      } finally {
        this.fallbackChunks = []
        this.fallbackBuffer = null
        this.appendQueue = []
      }
      return
    }

    // MSE path: If SourceBuffer is idle and queue empty, end immediately (only when open)
    if (
      this.mediaSource &&
      this.mediaSource.readyState === 'open' &&
      this.sourceBuffer &&
      !this.sourceBuffer.updating &&
      this.appendQueue.length === 0
    ) {
      try {
        this.mediaSource.endOfStream()
        this.isEnded = true
        // eslint-disable-next-line no-console
        console.log('[StreamingAudioPlayer] endOfStream() called')
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * Public API: reset MediaSource/SourceBuffer for a new streaming session.
   */
  public resetStream(): void {
    // reset counters
    this.totalBytesReceived = 0
    this.fallbackBytesTotal = 0
    this.fallbackBuffer = null
    this.initMediaSource()
  }

  /**
   * Public API: load a complete audio buffer and set it as the player source (non-MSE path).
   * Accepts raw bytes and mime; creates a Blob URL and loads the element.
   * Does NOT re-initialize visualizer; the analyser remains bound to the <audio> element.
   */
  public loadBytes(bytes: Uint8Array, mime: string, autoplay = false): void {
    const audio = this.$refs.player
    if (!audio || !bytes || bytes.byteLength === 0) return
    try {
      // Update totalBytesReceived to enable the play button
      this.totalBytesReceived = bytes.byteLength
      // Reset state flags to allow playback
      this.isEnded = false
      this.playing_ = false
      
      // Build ArrayBuffer slice correctly in case of subarray view
      const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
      const blob = new Blob([ab], { type: mime })
      // Revoke previous URL if any
      if (this.objectUrl) {
        try { URL.revokeObjectURL(this.objectUrl) } catch { /* ignore */ }
        this.objectUrl = null
      }
      const url = URL.createObjectURL(blob)
      this.objectUrl = url
      try { audio.pause() } catch { /* ignore */ }
      try { audio.currentTime = 0 } catch { /* ignore */ }
      audio.src = url
      audio.load()
      try { audio.muted = false } catch { /* ignore */ }
      if (autoplay) {
        this.playAudio()
      }
    } catch {
      // No-op
    }
  }

  /**
   * Prime autoplay under a user gesture.
   * Call this synchronously in the click handler (before awaiting Promises)
   * to satisfy browser autoplay policies. It plays muted then pauses immediately.
   */
  public prime(): void {
    const audio = this.$refs.player
    if (!audio) return
    try {
      audio.muted = true
      const p = audio.play()
      if (p && typeof p.then === 'function') {
        p.then(() => {
          try { audio.pause() } catch { /* ignore */ }
          this.playing_ = false
        }).catch(() => { /* ignore */ })
      }
    } catch { /* ignore */ }
  }

  private concatChunks(chunks: Uint8Array[]): Uint8Array {
    const total = chunks.reduce((sum, c) => sum + c.byteLength, 0)
    const out = new Uint8Array(total)
    let offset = 0
    for (const c of chunks) {
      out.set(c, offset)
      offset += c.byteLength
    }
    return out
  }

  private appendToFallback(bytes: Uint8Array): void {
    if (!bytes || bytes.byteLength === 0) return
    if (!this.fallbackBuffer) {
      this.fallbackBuffer = new Uint8Array(bytes.byteLength)
      this.fallbackBuffer.set(bytes)
    } else {
      const merged = new Uint8Array(this.fallbackBuffer.byteLength + bytes.byteLength)
      merged.set(this.fallbackBuffer, 0)
      merged.set(bytes, this.fallbackBuffer.byteLength)
      this.fallbackBuffer = merged
    }
    this.fallbackBytesTotal += bytes.byteLength
    // eslint-disable-next-line no-console
    console.log('[StreamingAudioPlayer] appendToFallback', { added: bytes.byteLength, fallbackBytesTotal: this.fallbackBytesTotal, bufferLen: this.fallbackBuffer?.byteLength || 0 })
  }

  private transferQueueToFallback(): void {
    if (this.appendQueue.length > 0) {
      // Move all queued MSE bytes into fallback buffer
      while (this.appendQueue.length > 0) {
        const next = this.appendQueue.shift()
        if (next) this.appendToFallback(next)
      }
    }
  }

  private teardownMediaSource(): void {
    try {
      if (this.sourceBuffer) {
        try {
          this.sourceBuffer.removeEventListener('updateend', this.onUpdateEnd)
          if (this.sourceBuffer.updating) {
            // abort inflight updates if any
            this.sourceBuffer.abort()
          }
        } catch { /* ignore */ }
      }
      if (this.mediaSource) {
        try {
          // best-effort: if not ended, try to end
          if (this.mediaSource.readyState === 'open') {
            this.mediaSource.endOfStream()
          }
        } catch { /* ignore */ }
      }
    } finally {
      this.sourceBuffer = null
      this.mediaSource = null
      this.appendQueue = []
      this.endRequested = false
      this.firstAppended = false
      this.isEnded = true
      this.playing_ = false
      this.sourceOpen = false
      this.chosenMime = null
      this.fallbackMode = false
      this.fallbackChunks = []
      this.fallbackBuffer = null
      if (this.objectUrl) {
        try { URL.revokeObjectURL(this.objectUrl) } catch { /* ignore */ }
        this.objectUrl = null
      }
      // Cleanup WebAudio if used
      try {
        if (this.webAudioSource) {
          try { this.webAudioSource.stop(0) } catch { /* ignore */ }
          this.webAudioSource.disconnect()
        }
        this.webAudioSource = null
        if (this.audioCtx) {
          try { this.audioCtx.close() } catch { /* ignore */ }
        }
        this.audioCtx = null
      } catch { /* ignore */ }
    }
  }

  public playAudio(): void {
    const audio = this.$refs.player
    if (!audio) return
    // eslint-disable-next-line no-console
    console.log('[StreamingAudioPlayer] playAudio() invoked', { muted: audio.muted, srcSet: !!audio.src })
    const p = audio.play()
    if (p && typeof p.then === 'function') {
      p.then(() => {
        this.playing_ = true
      }).catch(() => {
        // Retry muted to satisfy autoplay policies, then unmute shortly after if already first chunk arrived
        try {
          audio.muted = true
          const retry = audio.play()
          if (retry && typeof retry.then === 'function') {
            retry.then(() => { this.playing_ = true }).catch(() => { /* ignore */ })
          }
          // Unmute after a short delay if we already appended first chunk
          if (this.firstAppended) {
            window.setTimeout(() => {
              try { audio.muted = false } catch { /* ignore */ }
            }, 150)
          }
        } catch { /* ignore */ }
      })
    }
  }

  togglePlay(): void {
    const audio = this.$refs.player
    if (!audio) return
    if (audio.paused) {
      const p = audio.play()
      if (p && typeof p.then === 'function') {
        p.then(() => { this.playing_ = true }).catch(() => { /* ignored: autoplay policy requires user gesture */ })
      } else {
        this.playing_ = !audio.paused
      }
    } else {
      audio.pause()
      this.playing_ = false
    }
  }

  private onPlay = (): void => {
    this.playing_ = true
  }

  private onPlaying = (): void => {
    this.playing_ = true
    const inst = this.avInstance as { start?: () => void } | null
    try { inst?.start?.() } catch { /* ignore */ }
  }

  private onPause = (): void => {
    this.playing_ = false
    const inst = this.avInstance as { stop?: () => void } | null
    try { inst?.stop?.() } catch { /* ignore */ }
  }

  private onEnded = (): void => {
    this.playing_ = false
    this.isEnded = true
    const inst = this.avInstance as { stop?: () => void } | null
    try { inst?.stop?.() } catch { /* ignore */ }
    const audio = this.$refs?.player
    if (audio) {
      try { audio.currentTime = 0 } catch { /* ignore */ }
    }
    this.resetCanvas()
  }

  private onWebAudioEnded = (): void => {
    this.playing_ = false
    this.isEnded = true
    const inst = this.avInstance as { stop?: () => void } | null
    try { inst?.stop?.() } catch { /* ignore */ }
    this.resetCanvas()
  }

  private onAudioError = (): void => {
    const audio = this.$refs?.player
    const err = audio?.error
    // eslint-disable-next-line no-console
    console.error('Audio element error', err ? { code: err.code } : {})
  }

  private resetCanvas(): void {
    const canvas = this.$refs?.canvas
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  mounted(): void {
    // Bind audio events to keep icon state in sync
    const audio = this.$refs.player
    if (audio) {
      audio.addEventListener('play', this.onPlay)
      audio.addEventListener('playing', this.onPlaying)
      audio.addEventListener('pause', this.onPause)
      audio.addEventListener('ended', this.onEnded)
      audio.addEventListener('error', this.onAudioError)
    }

    // Initialize MediaSource and visualizer after layout
    nextTick(() => {
      this.initMediaSource()
      this.initVisualizer()
      // Do not call play here; wait for first chunk or user gesture
    })

    // Observe row width changes to keep canvas responsive
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        const w = this.getAvailableCanvasWidth() - 20
        this.setCanvasSize(w, this.canvHeight)
      })
      this.resizeObserver.observe(this.$refs.row)
    }
  }

  beforeUnmount(): void {
    const audio = this.$refs.player
    if (audio) {
      audio.removeEventListener('play', this.onPlay)
      audio.removeEventListener('playing', this.onPlaying)
      audio.removeEventListener('pause', this.onPause)
      audio.removeEventListener('ended', this.onEnded)
      audio.removeEventListener('error', this.onAudioError)
    }

    // Cleanup visualizer instance if supported
    const prev = this.avInstance as { destroy?: () => void; stop?: () => void } | null
    if (prev?.destroy) {
      try { prev.destroy() } catch { /* ignore */ }
    } else if (prev?.stop) {
      try { prev.stop() } catch { /* ignore */ }
    }
    this.avInstance = null

    // Cleanup MSE resources
    this.teardownMediaSource()

    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect() } catch { /* ignore */ }
      this.resizeObserver = undefined
    }
  }
}
</script>

<style scoped>
.audio-player {
  display: block;
  width: 100%;
  margin: 0;
  padding: 0;
}

.controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.controls-row canvas {
  display: block;
  flex: 1 1 auto;
}

.hidden-audio {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.controls-row :deep(.v-btn:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
